import { SagaIterator } from 'redux-saga';
import { call, delay, put } from 'redux-saga/effects';
import { DevBoxAddOnActionState } from '../../../constants/add-ons';
import { GetDevBoxAddOnResponse, getDevBoxAddOn } from '../../../data/services/data-plane-api/add-ons';
import { ClientError, FailureOperation, isFailureResponse } from '../../../models/common';
import {
    pollNonTerminalDevBoxAddOn,
    pollNonTerminalDevBoxAddOnComplete,
    pollNonTerminalDevBoxAddOnError,
} from '../../actions/add-ons/add-ons-action-creators';
import { PollNonTerminalDevBoxAddOnAction } from '../../actions/add-ons/add-ons-actions';
import { getAccessToken } from '../../actions/identity/identity-action-creators';
import { GetAccessTokenForDevCenterDataPlanePayload } from '../../actions/identity/identity-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeEvery } from '../../effects/take';
import { getPollingInitialDelayMs, getPollingIntervalMs } from '../../utilities/poll-action';

export function* pollNonTerminalDevBoxAddOnSaga(action: PollNonTerminalDevBoxAddOnAction): SagaIterator {
    let shouldPoll = true;
    const { meta, payload } = action;
    const { activityId } = meta ?? {};
    const { id, name } = payload;

    try {
        yield delay(getPollingInitialDelayMs(action));

        const pollingInterval = getPollingIntervalMs(action);

        while (shouldPoll) {
            const accessToken: string = yield putAndAwait(
                getAccessToken(GetAccessTokenForDevCenterDataPlanePayload(), meta)
            );
            const response: GetDevBoxAddOnResponse = yield call(getDevBoxAddOn, id, name, accessToken, activityId);

            if (isFailureResponse(response)) {
                yield put(pollNonTerminalDevBoxAddOnComplete({ id }, meta));
                yield resolveAction(action, response);
                return;
            }

            const { data } = response;
            const { actionState } = data;

            shouldPoll = actionState === DevBoxAddOnActionState.Enabling;

            if (shouldPoll) {
                yield delay(pollingInterval);
            } else {
                yield put(pollNonTerminalDevBoxAddOnComplete({ id, result: data }, meta));
                yield resolveAction(action, response);
            }
        }
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.PollNonTerminalDevBoxHyperV);
        yield put(pollNonTerminalDevBoxAddOnError({ error, id }, meta));
        yield rejectAction(action, error);
    }
}

export function* pollNonTerminalDevBoxAddOnListenerSaga(): SagaIterator {
    yield takeEvery(pollNonTerminalDevBoxAddOn, pollNonTerminalDevBoxAddOnSaga);
}
